import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/daglb7rep053ijub2ck0";

type Status = "idle" | "success" | "error";

export default function Newsletter() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 蜜罐：若被自动填充，则静默按成功处理，不向服务端发送
    const trap = String(fd.get("website_alt") || "").trim();
    if (trap) {
      setStatus("success");
      setFormError("");
      return;
    }
    fd.delete("website_alt");

    setBusy(true);
    setStatus("idle");
    setFormError("");
    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = (parsed as { code?: string } | null)?.code;
      const msg =
        (parsed as { meta?: { message?: string; detail?: string } } | null)?.meta?.message ||
        (parsed as { message?: string } | null)?.message ||
        responseText;

      const lower = (msg || "").toLowerCase();
      const serverSpam = lower.includes("spam") || lower.includes("form data is spam");

      if (response.ok && code === "OK" && !serverSpam) {
        setStatus("success");
        form.reset();
        return;
      }
      setStatus("error");
      setFormError(typeof msg === "string" && msg.trim() ? msg : "提交失败，请稍后再试。");
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section id="subscribe" className="px-4 py-12 md:px-6 md:py-16">
      <div className="relative mx-auto w-full max-w-6xl overflow-hidden rounded-3xl bg-primary-500 px-5 py-10 md:px-10 md:py-14">
        <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -bottom-16 left-1/3 h-56 w-56 rounded-full bg-accent-400/20" />
        <div className="pointer-events-none absolute right-10 top-8 text-white/20">
          <i className="ri-quill-pen-fill animate-float text-6xl" aria-hidden="true" />
        </div>

        <div className="relative flex flex-col items-start gap-6 text-left lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-bold text-white">
              <i className="ri-mail-send-fill" aria-hidden="true" />
              瓜报周讯 · 每周一送达
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black leading-snug text-white md:text-3xl">
              不想天天刷？每周一份瓜报直接发你邮箱
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/85">
              我们每周把「本周瓜汇总」与「本月盘点」整理好，轻轻松松发到你的邮箱，几分钟看完，追热点不掉队，退订也一键搞定。
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form="瓜报周讯订阅"
            data-readdy-form-id="daglb7rep053ijub2ck0"
            className="w-full max-w-md"
            noValidate
          >
            <div className="flex flex-col gap-3 sm:flex-row">
              <label htmlFor="subscribe-email" className="sr-only">
                你的邮箱
              </label>
              <input
                id="subscribe-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="输入你的邮箱"
                className="w-full flex-1 rounded-full border border-white/30 bg-white/15 px-5 py-3 text-sm text-white placeholder-white/70 outline-none backdrop-blur-sm transition-colors focus:border-white focus:bg-white/25"
              />
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-white px-6 py-3 text-sm font-black text-primary-600 transition-colors hover:bg-secondary-100 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {busy ? "订阅中…" : "立即订阅"}
                <i className="ri-arrow-right-line ml-2" aria-hidden="true" />
              </button>
            </div>
            {/* 反垃圾蜜罐 */}
            <div className="field-trap" aria-hidden="true">
              <label htmlFor="website_alt">Website</label>
              <input
                id="website_alt"
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                readOnly
                aria-hidden="true"
              />
            </div>

            {status === "success" ? (
              <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold text-white">
                <i className="ri-checkbox-circle-fill text-accent-200" aria-hidden="true" />
                订阅成功，下周瓜报记得查收！
              </p>
            ) : null}
            {status === "error" ? (
              <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary-950/30 px-3 py-1.5 text-xs font-bold text-white">
                <i className="ri-close-circle-fill" aria-hidden="true" />
                {formError}
              </p>
            ) : null}
          </form>
        </div>
      </div>
    </section>
  );
}